import { Pressable } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { Event } from '@/feature/event-details-sbk/types';
import { TeamOrder } from '@/utils/sport-utils';

import { ds } from '../lib/design.system';
import { MarketColumns } from './parts/MarketColumns';
import { TEAM_ROW_HEIGHT } from './parts/TeamInfoRow';
import { TeamLogo } from './parts/TeamLogo';
import { TieInfoRow } from './parts/TieInfoRow';
import { useMatchupTeamRows } from './use-matchup-team-rows';
import { MarketColumn as MarketColumnData } from './utils';

type StaticMatchupCardProps = {
    event: Event;
    marketColumns: MarketColumnData[];
    totalRows: number;
    teamOrder: TeamOrder;
    showTieRow?: boolean;
    onPress?: () => void;
};

// Used in the event sheet screen. Shows participants and markets, no live scores or status bar.
export const StaticMatchupCard = ({
    event,
    marketColumns,
    totalRows,
    teamOrder,
    showTieRow,
    onPress,
}: StaticMatchupCardProps) => {
    const teamInfoRows = useMatchupTeamRows(event, teamOrder);

    return (
        <Box gap="s16" pb="s24" mx="s16" borderBottomWidth={1} borderBottomColor={ds.colorToken.border.strong}>
            <Row justifyContent="space-between" alignItems="flex-end" gap="s16">
                {/** Team Name Columns */}
                <Column flex={1}>
                    <Pressable onPress={onPress}>
                        <Column gap="s3" flex={1}>
                            {teamInfoRows.map(
                                ({ side, teamName, logoUrl, primaryColor, secondaryColor, shouldShowFallbackLogo }) => (
                                    <Row height={TEAM_ROW_HEIGHT} alignItems="center" gap="s8" key={side}>
                                        <TeamLogo
                                            logoUrl={logoUrl}
                                            primaryColor={primaryColor}
                                            secondaryColor={secondaryColor}
                                            shouldShowFallbackLogo={shouldShowFallbackLogo}
                                        />
                                        <Row justifyContent="space-between" flex={1}>
                                            <Column flexShrink={1}>
                                                <Row>
                                                    <Text
                                                        variant="titleMedium"
                                                        color={ds.colorToken.text.primary}
                                                        numberOfLines={1}
                                                    >
                                                        {teamName}
                                                    </Text>
                                                </Row>
                                            </Column>
                                        </Row>
                                    </Row>
                                )
                            )}
                            {showTieRow ? <TieInfoRow /> : null}
                        </Column>
                    </Pressable>
                </Column>
                {/** Market Columns */}
                <MarketColumns
                    event={event}
                    marketColumns={marketColumns}
                    totalRows={totalRows}
                    teamOrder={teamOrder}
                    showHeader={true}
                />
            </Row>
        </Box>
    );
};
